// Singles: the picker scanned a new york while the order they are in the
// middle of still has units to pick. Swapping now would put the rest of
// that order into the second york, and a Singles york is packed straight
// from the york — the packer then has to go and find the other one
// (2026-10-12, Glyn Davies, wave 10395: "the order is spread between
// two yorks").
//
// The BE refused the swap with order_part_picked and told us what is
// left; this card shows it so the picker can see whether the rest fits
// in the york they have. Two exits only:
//   • Keep picking   — dismiss, carry on into the current york
//   • Swap anyway    — re-post the tote scan with force (the remaining
//                      units genuinely don't fit)
// Scanning the SAME york again while the card is up also means "swap
// anyway" (scanner-first, no reaching for the screen); any other scan
// dismisses the card and is processed as normal. The host owns both.
import { Split, ArrowRightLeft, PackageOpen } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { PartPickedOrder } from "../../hooks/usePickingSession";

interface Props {
    order: PartPickedOrder;
    /** The york the picker just scanned (the one the swap would open). */
    newToteId: string;
    busy?: boolean;
    onKeepPicking: () => void;
    onSwapAnyway: () => void;
}

const PartPickedSwapCard = ({ order, newToteId, busy, onKeepPicking, onSwapAnyway }: Props) => {
    const { t } = useTranslation();
    const remainingUnits = order.remaining.reduce((n, l) => n + Math.max(0, l.qty_remaining), 0);

    return (
        <div className="picking-part-picked" data-testid="part-picked-swap-card">
            <div className="picking-part-picked-icon">
                <Split size={72} strokeWidth={1.4} aria-hidden="true" />
            </div>
            <h2 className="picking-part-picked-title">{t("picking_part_picked_title")}</h2>
            <p className="picking-part-picked-detail">
                {t("picking_part_picked_body", {
                    picked: String(order.qtyPicked),
                    required: String(order.qtyRequired),
                    tote: order.toteId,
                })}
            </p>

            <div className="picking-part-picked-list" role="list" aria-label={t("picking_part_picked_left", { count: String(remainingUnits) })}>
                <div className="picking-part-picked-list-head">
                    {t("picking_part_picked_left", { count: String(remainingUnits) })}
                </div>
                {order.remaining.map(line => (
                    <div key={line.line_id} className="picking-part-picked-line" role="listitem">
                        <span className="picking-part-picked-qty">{line.qty_remaining}×</span>
                        <span className="picking-part-picked-item">
                            <span className="picking-part-picked-sku">{line.sku}</span>
                            {line.product_title && (
                                <span className="picking-part-picked-name">{line.product_title}</span>
                            )}
                        </span>
                        <span className="picking-part-picked-loc">{line.location_display || line.location}</span>
                    </div>
                ))}
            </div>

            <div className="picking-part-picked-actions">
                <button
                    type="button"
                    className="btn btn-primary btn-lg picking-part-picked-btn"
                    onClick={onKeepPicking}
                    disabled={busy}
                    autoFocus
                >
                    <PackageOpen size={20} aria-hidden="true" />
                    {t("picking_part_picked_keep", { tote: order.toteId })}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost picking-part-picked-btn picking-part-picked-btn--swap"
                    onClick={onSwapAnyway}
                    disabled={busy}
                >
                    <ArrowRightLeft size={16} aria-hidden="true" />
                    {t("picking_part_picked_swap", { tote: newToteId })}
                </button>
            </div>
            <p className="picking-part-picked-hint">
                {t("picking_part_picked_hint", { tote: newToteId })}
            </p>
        </div>
    );
};

export default PartPickedSwapCard;
